/* global BigInt */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { CONTRACTS, DURATIONS } from '../config/contracts';
import {
  formatUSDC,
  parseUSDC,
  formatAPY,
  formatBlocksRemaining,
  getOpportunityStatus,
  generateOptionLabels,
  mapOpportunity
} from '../utils/helpers';
import StatusBadge from '../components/StatusBadge';
import './Opportunity.css';

function Opportunity() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    address,
    provider,
    marketContract,
    positionTokenContract,
    usdcContract
  } = useWeb3();

  const [opportunity, setOpportunity] = useState(null);
  const [stakes, setStakes] = useState([]);
  const [bettorCounts, setBettorCounts] = useState([0n, 0n, 0n, 0n, 0n, 0n]);
  const [userStakes, setUserStakes] = useState([0n, 0n, 0n, 0n, 0n, 0n]);
  const [currentBlock, setCurrentBlock] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedOption, setSelectedOption] = useState(null);
  const [betAmount, setBetAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [betStep, setBetStep] = useState(null); // null, 'approving', 'approved', 'betting', 'complete'
  const [claiming, setClaiming] = useState(false);

  const loadOpportunity = useCallback(async () => {
    if (!marketContract) return;

    try {
      // Get current block
      if (provider) {
        const block = await provider.getBlockNumber();
        setCurrentBlock(block);
      }

      const [opp, oppStakes, oppBettors] = await Promise.all([
        marketContract.getOpportunity(id),
        marketContract.getTotalStakePerOption(id),
        marketContract.getUniqueBettorsPerOption(id)
      ]);

      setOpportunity(mapOpportunity(opp, Number(id)));
      setStakes(Array.from(oppStakes));
      setBettorCounts(Array.from(oppBettors));

      // Load user stakes from ERC1155 balances
      if (address && positionTokenContract) {
        const userStakeArr = [];
        for (let i = 0; i < 6; i++) {
          const tokenId = BigInt(id) * 10n + BigInt(i);
          const balance = await positionTokenContract.balanceOf(address, tokenId);
          userStakeArr.push(balance);
        }
        setUserStakes(userStakeArr);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading opportunity:', error);
      setLoading(false);
    }
  }, [marketContract, positionTokenContract, provider, address, id]);

  useEffect(() => {
    loadOpportunity();
    const interval = setInterval(loadOpportunity, 10000);
    return () => clearInterval(interval);
  }, [loadOpportunity]);

  const placeBet = async () => {
    if (!marketContract || !usdcContract || selectedOption === null || !betAmount) return;

    const numAmount = parseFloat(betAmount);
    if (isNaN(numAmount) || numAmount < 1) {
      alert('Minimum bet is 1 USDC');
      return;
    }

    setPlacing(true);
    setBetStep(null);
    try {
      const amount = parseUSDC(betAmount);

      // Check and approve USDC
      const allowance = await usdcContract.allowance(address, CONTRACTS.MARKET_ADDRESS);
      if (allowance < amount) {
        setBetStep('approving');
        const approveTx = await usdcContract.approve(CONTRACTS.MARKET_ADDRESS, amount);
        await approveTx.wait();
        setBetStep('approved');
      }

      // Place bet
      setBetStep('betting');
      const tx = await marketContract.placeBet(id, selectedOption, amount);
      await tx.wait();
      setBetStep('complete');

      // Reload data
      setBetAmount('');
      setSelectedOption(null);
      setTimeout(loadOpportunity, 2000);
    } catch (error) {
      console.error('Error placing bet:', error);
      alert('Bet failed: ' + error.message);
    } finally {
      setPlacing(false);
      setBetStep(null);
    }
  };

  const claimPayout = async () => {
    if (!positionTokenContract || !address) return;

    setClaiming(true);
    try {
      // Collect tokens to transfer
      const tokenIds = [];
      const amounts = [];

      if (opportunity.voided) {
        // Voided: transfer all options user has
        for (let i = 0; i < 6; i++) {
          const tokenId = await marketContract.getTokenId(id, i);
          const balance = await positionTokenContract.balanceOf(address, tokenId);
          if (balance > 0n) {
            tokenIds.push(tokenId);
            amounts.push(balance);
          }
        }
      } else {
        // Not voided: transfer only winning option
        const winningOption = opportunity.winningOption;
        const tokenId = await marketContract.getTokenId(id, winningOption);
        const balance = await positionTokenContract.balanceOf(address, tokenId);
        if (balance > 0n) {
          tokenIds.push(tokenId);
          amounts.push(balance);
        }
      }

      if (tokenIds.length === 0) {
        throw new Error('No tokens to claim');
      }

      // Transfer tokens to market contract (triggers claim via hook)
      let tx;
      if (tokenIds.length === 1) {
        tx = await positionTokenContract.safeTransferFrom(
          address,
          CONTRACTS.MARKET_ADDRESS,
          tokenIds[0],
          amounts[0],
          '0x'
        );
      } else {
        tx = await positionTokenContract.safeBatchTransferFrom(
          address,
          CONTRACTS.MARKET_ADDRESS,
          tokenIds,
          amounts,
          '0x'
        );
      }

      await tx.wait();
      setTimeout(loadOpportunity, 2000);
    } catch (error) {
      console.error('Error claiming:', error);
      alert('Claim failed: ' + error.message);
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading opportunity...</div>;
  }

  if (!opportunity) {
    return <div className="loading">Opportunity not found.</div>;
  }

  const status = getOpportunityStatus(opportunity, currentBlock);
  const totalVolume = stakes.reduce((sum, stake) => sum + stake, 0n);
  const totalBettors = bettorCounts.reduce((sum, count) => sum + Number(count || 0n), 0);
  const optionLabels = generateOptionLabels(opportunity.thresholds, opportunity.startApy);
  const canBet = status === 'betting_open' && address;

  // Check if user can claim
  const totalUserStake = userStakes.reduce((sum, s) => sum + s, 0n);
  const canClaim = status === 'resolved' && totalUserStake > 0n && (
    opportunity.voided || userStakes[opportunity.winningOption] > 0n
  );

  const observationStart = currentBlock ? formatBlocksRemaining(currentBlock, opportunity.startBlock) : 'Soon';
  const observationEnd = currentBlock ? formatBlocksRemaining(currentBlock, opportunity.endBlock) : 'Soon';

  return (
    <div className="opportunity-page">
      <button className="back-btn" onClick={() => navigate('/')}>
        ← Back to Markets
      </button>

      <div className="opportunity-header">
        <div className="opp-title">
          <h1>{opportunity.name}</h1>
          <span className="duration-badge">
            {DURATIONS[Number(opportunity.durationEnum)]?.shortLabel || '?'}
          </span>
          <span className="bettors-badge">👤 {totalBettors}</span>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Betting With Respect To</div>
          <div className="stat-value highlight">{formatAPY(opportunity.startApy)}</div>
        </div>
        {opportunity.resolved && !opportunity.voided && (
          <div className="stat-card">
            <div className="stat-label">Final APY</div>
            <div className="stat-value highlight">{formatAPY(opportunity.finalApy)}</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-label">Total Volume</div>
          <div className="stat-value">${formatUSDC(totalVolume)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Observation Starts</div>
          <div className="stat-value">{status === 'betting_open' ? observationStart : 'Started'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Observation Ends</div>
          <div className="stat-value">{status === 'resolved' ? 'Resolved' : observationEnd}</div>
        </div>
      </div>

      <div className="options-section">
        <h2>Betting Options</h2>
        <div className="options-grid">
          {stakes.map((stake, index) => {
            const option = optionLabels[index];
            if (!option) return null;

            const isWinner = opportunity.resolved && !opportunity.voided && Number(opportunity.winningOption) === index;
            const isSelected = selectedOption === index;
            const userStake = userStakes[index];
            const hasBet = userStake > 0n;
            const bettorCount = Number(bettorCounts[index] || 0n);

            return (
              <div
                key={index}
                className={`option-card ${option.type} ${isWinner ? 'winner' : ''} ${isSelected ? 'selected' : ''} ${hasBet ? 'has-bet' : ''} ${canBet ? 'clickable' : ''}`}
                title={option.tooltip}
                onClick={() => canBet && setSelectedOption(index)}
              >
                <div className="option-main">{option.main}</div>
                <div className="option-sub">{option.sub}</div>
                <div className="option-footer">
                  <span className="option-bettors">👤 {bettorCount}</span>
                  <span className="option-stake">${formatUSDC(stake)}</span>
                </div>
                {hasBet && (
                  <div className="option-user-stake">Your bet: ${formatUSDC(userStake)}</div>
                )}
                {isWinner && <div className="winner-badge">WINNER</div>}
              </div>
            );
          })}
        </div>
      </div>

      {canBet && (
        <div className="betting-section">
          <h2>Place Your Bet</h2>
          <div className="bet-form">
            <div className="selected-option">
              {selectedOption !== null ? (
                <span>Selected: <strong>{optionLabels[selectedOption]?.main}</strong></span>
              ) : (
                <span>Click an option above to select</span>
              )}
            </div>
            <div className="bet-input-row">
              <input
                type="number"
                placeholder="Min 1 USDC"
                min="1"
                step="1"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                disabled={selectedOption === null || placing}
              />
              <button
                className="bet-btn"
                onClick={placeBet}
                disabled={!betAmount || parseFloat(betAmount) < 1 || selectedOption === null || placing}
              >
                {placing ? (
                  betStep === 'approving' ? 'Step 1/2: Approving USDC...' :
                  betStep === 'approved' ? 'Step 1/2: Approved!' :
                  betStep === 'betting' ? 'Step 2/2: Placing Bet...' :
                  betStep === 'complete' ? 'Complete!' :
                  'Processing...'
                ) : 'Place Bet'}
              </button>
            </div>
            {placing && (
              <div className="bet-progress">
                <div className="progress-steps">
                  <div className={`progress-step ${betStep === 'approving' ? 'active' : ''} ${betStep === 'approved' || betStep === 'betting' || betStep === 'complete' ? 'done' : ''}`}>
                    <div className="step-number">1</div>
                    <div className="step-label">Approve USDC</div>
                  </div>
                  <div className="progress-line"></div>
                  <div className={`progress-step ${betStep === 'betting' ? 'active' : ''} ${betStep === 'complete' ? 'done' : ''}`}>
                    <div className="step-number">2</div>
                    <div className="step-label">Place Bet</div>
                  </div>
                </div>
                <div className="progress-hint">
                  {betStep === 'approving' && 'Please confirm the approval transaction in your wallet...'}
                  {betStep === 'approved' && 'Approval confirmed! Preparing bet transaction...'}
                  {betStep === 'betting' && 'Please confirm the bet transaction in your wallet...'}
                  {betStep === 'complete' && 'Bet placed successfully!'}
                  {!betStep && 'Checking allowance...'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {canClaim && (
        <div className="claim-section">
          <button
            className="claim-btn"
            onClick={claimPayout}
            disabled={claiming}
          >
            {claiming ? 'Claiming...' : opportunity.voided ? 'Claim Refund' : 'Claim Winnings'}
          </button>
        </div>
      )}

      {opportunity.resolved && !opportunity.voided && (
        <div className="resolution-info">
          <h3>Resolution Details</h3>
          <p>Final APY: <strong>{formatAPY(opportunity.finalApy)}</strong></p>
          <p>Winning Option: <strong>{optionLabels[Number(opportunity.winningOption)]?.main}</strong></p>
          <p>Payout Multiplier: <strong>{(Number(opportunity.rewardPerToken) / 1e18).toFixed(4)}x</strong></p>
        </div>
      )}
    </div>
  );
}

export default Opportunity;
